import React, { type ReactNode, useMemo } from 'react';

import { Tooltip } from '@mui/material';
import {
  DataGridPremium,
  GRID_TREE_DATA_GROUPING_FIELD,
  type GridColDef,
} from '@mui/x-data-grid-premium';
import { type GridApiPremium } from '@mui/x-data-grid-premium/models/gridApiPremium';
import { format } from 'date-fns';
import _, { isNumber } from 'lodash';
import numbro from 'numbro';

import { ModelCompareTooltip } from '../common/dataTableFormatter';
import { type OutputSchemaColumn } from '../common/dataTypes';
import cn from '../utils/cn';

export type DataGridPremiumTableColumn = {
  name: string;
  label: string;
  is_pinned: boolean;
  data_type: 'number' | 'string' | 'date';
  styles: OutputSchemaColumn['styles'];
};

export type CompareCellType = {
  trend: 'up' | 'down' | 'equal';
  value_a: number | '__empty__';
  value_b: number | '__empty__';
  value_diff: number;
};

export type DataGridPremiumTableProps = {
  apiRef: React.MutableRefObject<GridApiPremium>;
  columns: DataGridPremiumTableColumn[];
  actualDataColumns?: Set<string>;
  treeData?: {
    headerName: string;
    groupColumns: string[];
  };
  isLoading: boolean;
  placeholder?: React.ReactNode;
  data?: Record<string, (string | number | CompareCellType)[]> | undefined;
  compareData?:
    | Record<
        string,
        {
          trend: 'up' | 'down' | 'equal';
          value_a: number;
          value_b: number;
          value_diff: number;
        }[]
      >
    | undefined;
  base?: {
    data: Record<string, (string | number)[]> | undefined;
    mode: 'vector' | 'actual';
  };
};

const CompareCell = ({
  value_diff,
  value_a,
  value_b,
  trend,
  numberFormat,
}: CompareCellType & {
  numberFormat: Record<string, any> | undefined;
}) => {
  let result;
  let color;

  if (value_diff === 0) {
    result = '-';
  } else if (trend === 'up') {
    result = numbro(value_diff).format(numberFormat);
    color = 'rgb(46 161 46)';
  } else if (trend === 'down') {
    result = `(${numbro(value_diff).format(numberFormat)})`;
    color = '#ff0000';
  }

  return (
    <ModelCompareTooltip
      numberA={value_a}
      numberB={value_b}
      numberDiff={value_diff}
    >
      <span
        style={{
          color,
        }}
      >
        {result}
      </span>
    </ModelCompareTooltip>
  );
};

const VectorCell = ({
  baseValue,
  value,
  isPinned,
  numberFormat,
}: {
  value: number;
  baseValue: number;
  isPinned: boolean;
  numberFormat: Record<string, any> | undefined;
}) => {
  if (isPinned) {
    return <div>{value}</div>;
  }

  return (
    <Tooltip
      disableFocusListener
      title={
        <span className="text-[0.875rem] w-full">
          Base value: {numbro(baseValue).format(numberFormat ?? {})}
        </span>
      }
    >
      <div
        style={
          value === baseValue
            ? { color: '#000000' }
            : value > baseValue
              ? { color: 'rgb(46 161 46)' }
              : { color: '#ff0000' }
        }
      >
        <span>{numbro(value).format(numberFormat ?? {})}</span>
      </div>
    </Tooltip>
  );
};

const FormatRow = ({
  isPinned,
  children,
  data_type,
}: {
  isPinned: boolean;
  children: ReactNode;
  data_type: 'number' | 'string' | 'date';
}) => {
  let result = children;

  if (data_type === 'date' && children) {
    result = format(new Date(Number(children)).toLocaleDateString(), 'y/MM/dd');
  }

  if (isPinned === false && (result === 0 || result === '0')) {
    result = '-';
  }

  return (
    <div
      className={cn('items-center justify-end flex w-full', {
        'justify-start': isPinned,
      })}
    >
      {result}
    </div>
  );
};

const getRows = ({
  data,
  columns,
}: {
  data: Record<string, (string | number | CompareCellType)[]>;
  columns: DataGridPremiumTableProps['columns'];
}) =>
  columns.reduce(
    (acc, cur) => {
      const { name } = cur;

      data[name]?.forEach(
        (value, index) =>
          (acc[index] = {
            id: `${name}__${index}`,
            ...acc[index],
            type: data.id?.[index] ?? 'row',
            [name]: value,
          })
      );

      return acc;
    },
    [] as Record<string, string | number | CompareCellType>[]
  );

const getGroupRows = ({
  rows,
  treeData,
}: {
  rows: Record<string, string | number | CompareCellType>[];
  treeData: DataGridPremiumTableProps['treeData'];
}) =>
  rows.flatMap((a) => {
    const path = treeData?.groupColumns
      .map((c) => a[c])
      .filter((i) => i !== 'all');

    return {
      ...(treeData?.groupColumns ? _.omit(a, treeData?.groupColumns) : a),
      path: path?.length ? path : ['Total'],
    } as unknown as Record<string, string | number | (string[] | undefined)>[];
  });

const DataGridPremiumTable = ({
  columns,
  actualDataColumns,
  data,
  apiRef,
  isLoading,
  base,
  treeData,
  placeholder,
}: DataGridPremiumTableProps) => {
  const baseMode = base?.mode;

  const rows = useMemo(
    () =>
      data
        ? getRows({
            data,
            columns,
          })
        : [],
    [data, columns]
  );

  const baseRows = useMemo(
    () =>
      base?.data
        ? getRows({
            data: base.data,
            columns,
          })
        : [],
    [columns, base]
  );

  const groupRows = useMemo(
    () =>
      rows
        ? getGroupRows({
            rows,
            treeData,
          })
        : [],
    [rows, treeData]
  );

  const baseGroupRows = useMemo(
    () =>
      baseRows
        ? getGroupRows({
            rows: baseRows,
            treeData,
          })
        : [],
    [baseRows, treeData]
  );

  const cols = useMemo(
    () =>
      columns
        .filter(({ name }) => !treeData?.groupColumns.includes(name))
        .map(({ label, name, is_pinned, styles, data_type }) => {
          const mergedStyles = styles
            ? {
                numfmt: styles[styles?.length - 1]?.numfmt ?? {},
                row_filters: [...styles.map((s) => s.row_filters).flat()],
              }
            : {};

          const filters =
            styles
              ?.map((i) =>
                i.row_filters.map((r: any) => ({
                  ...r,
                  numfmt: i.numfmt,
                }))
              )
              .flat() ?? [];

          const rowFilters = mergedStyles.row_filters;

          let numberFormat = {} as Record<string, any> | undefined;

          const maxLength = groupRows.reduce((acc, cur) => {
            const valueLength = isNumber(cur[name])
              ? numbro(cur[name])
                  .format({
                    thousandSeparated: true,
                    mantissa: 2,
                  })
                  ?.toString().length
              : (cur[name]?.toString().length ?? 0);

            if (valueLength > acc) {
              acc = valueLength;
            }

            return acc;
          }, 0);

          return {
            field: name,
            headerName: label,
            flex: 100,
            minWidth: maxLength
              ? label.length >= maxLength
                ? label.length * 14
                : maxLength * 11
              : 140,
            align: 'right',
            headerAlign: is_pinned ? 'left' : 'right',
            hideable: false,
            headerClassName: actualDataColumns?.has(name)
              ? 'actual-header'
              : '',
            sortable: false,
            pinnable: is_pinned,
            filterable: false,
            disableColumnMenu: true,
            renderCell: (params) => {
              const id = params.id;
              const field = params.field;
              const value = params.value;

              const isComparing =
                typeof value === 'object' &&
                value !== null &&
                'value_a' in value;

              let baseValue;

              if (treeData && isComparing) {
                const baseGroupRow = baseGroupRows.find((b) =>
                  _.isEqual(b.path, groupRows[id as number]?.path)
                );

                baseValue = !baseGroupRows.length
                  ? undefined
                  : baseGroupRow
                    ? baseGroupRow[field]
                    : 0;
              }

              if (baseMode === 'vector') {
                const index = Number(
                  _.takeRight((id as string).split('_'), 1)[0]
                );

                baseValue = base?.data?.[field][index];
              }

              if (!rowFilters || rowFilters?.length === 0) {
                numberFormat = mergedStyles.numfmt;
              } else {
                const groupingKey = params.row.path?.[0];

                const rowFilter =
                  filters.find((r) => r.value === groupingKey) ??
                  filters.find((r) => params.row[r.field] === r.value);

                numberFormat = rowFilter?.numfmt;
              }

              if (!isNumber(value) && !isNumber(baseValue) && !isComparing) {
                const red = _.includes(value, 'SL');
                const blue = _.includes(value, 'TL');

                return (
                  <FormatRow isPinned={is_pinned} data_type={data_type}>
                    <p
                      className={cn('', {
                        'text-primary-red': red,
                        'text-[#2196F3]': blue,
                      })}
                    >
                      {value}
                    </p>
                  </FormatRow>
                );
              }

              if (!isNumber(baseValue) && !isComparing) {
                return (
                  <FormatRow isPinned={is_pinned} data_type={data_type}>
                    {numbro(value).format(numberFormat)}
                  </FormatRow>
                );
              }

              if (isComparing) {
                return (
                  <FormatRow isPinned={is_pinned} data_type={data_type}>
                    <CompareCell
                      value_b={value.value_b}
                      value_a={value.value_a}
                      value_diff={value.value_diff}
                      trend={value.trend}
                      numberFormat={numberFormat}
                    />
                  </FormatRow>
                );
              }

              switch (baseMode) {
                case 'vector':
                  return (
                    <FormatRow isPinned={is_pinned} data_type={data_type}>
                      <VectorCell
                        value={value}
                        isPinned={is_pinned}
                        baseValue={baseValue as number}
                        numberFormat={numberFormat}
                      />
                    </FormatRow>
                  );
                case 'actual':
                  return (
                    <FormatRow isPinned={is_pinned} data_type={data_type}>
                      <div className="opacity-40">{value}</div>
                    </FormatRow>
                  );
                default:
                  <FormatRow isPinned={is_pinned} data_type={data_type}>
                    {value}
                  </FormatRow>;
              }
            },
          };
        }) as GridColDef[],
    [base, columns, treeData, actualDataColumns]
  );

  const pinnedRow = groupRows.find((r) => r.type === 'Total');

  const numberOfRows = groupRows?.length ?? 1;
  const rowHeight = 36;
  const shouldFixHeight = window.innerHeight * 0.7 < numberOfRows * rowHeight;

  const height = numberOfRows * rowHeight;

  return (
    <div
      className="pb-6"
      style={{
        height: shouldFixHeight ? `70vh` : height ? height + 100 : '300px',
      }}
    >
      <DataGridPremium
        treeData={!!treeData}
        getCellClassName={(params) => {
          if (actualDataColumns?.has(params.field)) {
            return 'actual-cell';
          }
          return '';
        }}
        groupingColDef={
          treeData
            ? {
                headerName: treeData.headerName,
                width: 340,
                hideDescendantCount: true,
              }
            : {}
        }
        getTreeDataPath={(row) =>
          treeData ? (row.path?.length ? row.path : ['']) : [row.id]
        }
        pinnedRows={
          pinnedRow && {
            bottom: [
              {
                ...pinnedRow,
                path: ['Total'],
              },
            ],
          }
        }
        apiRef={apiRef}
        rows={groupRows?.length ? groupRows : []}
        columns={cols}
        components={{
          NoRowsOverlay: () => (
            <div className="h-full z-10 flex justify-center text-[#666666] font-semibold items-center">
              {placeholder}
            </div>
          ),
        }}
        pagination={false}
        loading={isLoading}
        rowGroupingColumnMode="multiple"
        getRowClassName={(params) => {
          const maxLevel = treeData?.groupColumns.length;
          const groupLevel = params.row.path?.length;

          const rowType = params.row.type;

          if (maxLevel === 3) {
            switch (groupLevel) {
              case 1:
                return 'first-level-group-row';
              case 2:
                return 'second-level-group-row';
              default:
                return '';
            }
          } else if (maxLevel === 2) {
            switch (groupLevel) {
              case 1:
                return 'first-level-group-row';
              default:
                return '';
            }
          }

          if (rowType === 'Total') {
            return 'total-row';
          }

          return '';
        }}
        sx={{
          '& .MuiDataGrid-columnHeaders': {
            backgroundColor: '#01285F',
            color: '#FFFFFF !important',
            fontWeight: 'bold',
          },

          '& .MuiDataGrid-pinnedColumnHeaders': {
            backgroundColor: '#01285F',
          },

          '&. MuiDataGrid-virtualScrollerContent': {
            height: '200px !important',
          },

          '& .MuiDataGrid-row.first-level-group-row': {
            backgroundColor: '#B1D0FC',
            fontWeight: 'bold',
          },

          '& .MuiDataGrid-row.second-level-group-row': {
            backgroundColor: '#CEE2FF',
          },

          '& .MuiDataGrid-row.total-row': {
            backgroundColor: '#B1D0FC',
            fontWeight: 'bold',
          },
          '& .actual-cell': {
            backgroundColor: 'rgba(207, 226, 243, 0.5)',
          },
          '& .actual-header': {
            backgroundColor: '#1B5E20',
          },
        }}
        hideFooter
        disableAggregation
        density="compact"
        initialState={{
          aggregation: { model: { gross: 'sum' } },
        }}
        pinnedColumns={{
          left: cols
            .filter((col) => col.pinnable)
            .map((col) => col.field)
            .concat(GRID_TREE_DATA_GROUPING_FIELD),
        }}
        experimentalFeatures={{ rowPinning: true }}
        defaultGroupingExpansionDepth={2}
      />
    </div>
  );
};

export default DataGridPremiumTable;
